import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Bet, FeedSource, Outcome, SpecialValueWithOutcomeInfo } from '../@types/api';
import API_ROUTES from '../constants/apiRoutes';
import { QUERY_KEYS } from '../constants';
import {
  extractSpecialValuesFromOutcomeName,
  getSpecialValuesDisplayValue,
  isHandicapMarket,
} from '../helpers/specialValues';
import { getData } from '../utils/api';
import { useMarketplace } from './useMarketplace';
import { FEED_SOURCE } from '../constants/enums';

type UseSpecialValueReofferProps = {
  bet: Bet;
};

type ReofferData = SpecialValueWithOutcomeInfo | Outcome;

export type ProcessedReofferItem = {
  id: string;
  odds: number;
  name: string;
  marketId: string;
};

export type SpecialValueReofferConfig = {
  getReofferData: (bet: Bet) => Promise<{ items: ReofferData[] }>;
  processReofferData: (data: ReofferData[], bet: Bet) => ProcessedReofferItem[];
};

export const REOFFER_FEED_CONFIGS: Record<FeedSource, SpecialValueReofferConfig> = {
  [FEED_SOURCE.ODDS_MATRIX]: {
    getReofferData: (bet: Bet) =>
      getData(API_ROUTES.OM_SPECIAL_VALUES_REOFFER, {
        eventId: bet.event.id,
        marketTypeId: bet.market.marketTypeId,
        outcomeTypeId: bet.outcome.outcomeTypeId,
        modelType: bet.specialValues?.[0].modelType,
        modelId: bet.specialValues?.[0].modelId,
      }),
    processReofferData: (data: ReofferData[], bet: Bet) => {
      const marketTypeName = bet.market.marketType.name;
      const isHandicap = isHandicapMarket(marketTypeName);

      const specialValues = data as SpecialValueWithOutcomeInfo[];

      return specialValues
        .map((specialValue) => {
          const displayValue = isHandicap
            ? getSpecialValuesDisplayValue(
                extractSpecialValuesFromOutcomeName(
                  specialValue.outcome.name,
                  specialValues,
                  isHandicap,
                  marketTypeName
                )
              )
            : specialValue.value;

          return {
            id: specialValue.outcome.id,
            odds: specialValue.outcome.odds,
            name: displayValue?.toString() || '',
            marketId: specialValue.marketId,
          };
        })
        .sort((a, b) => Number(b.name) - Number(a.name));
    },
  },
  [FEED_SOURCE.FEED_THE_BET]: {
    getReofferData: (bet: Bet) =>
      getData(API_ROUTES.FTB_SPECIAL_VALUES_REOFFER, {
        marketId: bet.market.id,
        outcomeTypeId: bet.outcome.outcomeTypeId,
        outcomeName: bet.outcome.name,
      }),
    processReofferData: (data: ReofferData[]) => {
      const outcomes = data as Outcome[];

      return outcomes
        .map((outcome) => ({
          id: outcome.id,
          odds: outcome.odds,
          name: outcome.properties?.specialValue || '',
          marketId: outcome.marketId,
        }))
        .sort((a, b) => Number(a.name) - Number(b.name));
    },
  },
};

export const useSpecialValueReoffer = ({ bet }: UseSpecialValueReofferProps) => {
  const { feedSource } = useMarketplace();
  const config = REOFFER_FEED_CONFIGS[feedSource];

  const { data, isPending } = useQuery({
    queryKey: [QUERY_KEYS.specialValues, feedSource, bet.id],
    queryFn: () => config.getReofferData(bet),
    select: (data) => data.items,
  });

  const processedData = useMemo(() => {
    if (!data) return [];

    return config.processReofferData(data, bet);
  }, [data, config, bet]);

  const options = useMemo(() => processedData.map((item) => ({ id: item.id, name: item.name })), [processedData]);

  const getOutcomeDetailsById = useMemo(
    () => (outcomeId: string) => {
      const item = processedData.find((item) => item.id === outcomeId);
      if (!item) {
        return { odds: '', name: '', marketId: '' };
      }
      return { odds: item.odds.toString(), name: item.name, marketId: item.marketId };
    },
    [processedData]
  );

  return {
    options,
    getOutcomeDetailsById,
    isLoading: isPending,
  };
};
